import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { AdminPage } from "@/components/AdminPage";
import { AdminTable, type Column } from "@/components/AdminTable";
import { StackedBarChart } from "@/components/StackedBarChart";
import { apiFetch } from "../lib/api";

const PURPOSE_LABEL: Record<string, string> = {
  hiring: "Contratação",
  rental: "Locação",
  commercial_partnership: "Parceria comercial",
  due_diligence: "Due diligence",
  other: "Outra",
};

type PurposeSeries = {
  purposes: string[];
  series: Array<Record<string, number | string>>;
};

type FeedbackSeries = {
  keys: string[];
  series: Array<Record<string, number | string>>;
};

function useSeries<T>(url: string): T | null | "error" {
  const [data, setData] = useState<T | null | "error">(null);
  useEffect(() => {
    let alive = true;
    apiFetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        if (alive) setData(d as T);
      })
      .catch(() => {
        if (alive) setData("error");
      });
    return () => {
      alive = false;
    };
  }, [url]);
  return data;
}

function PurposeChart() {
  const data = useSeries<PurposeSeries>(
    "/api/admin/series/verifications-by-purpose?days=30"
  );

  if (data === null) return <ChartSkeleton />;
  if (data === "error" || data.purposes.length === 0) {
    return (
      <div className="grid h-[260px] place-items-center rounded-2xl border border-border bg-surface text-sm text-text-tertiary">
        Sem dados no período.
      </div>
    );
  }
  const labels = Object.fromEntries(
    data.purposes.map((p) => [p, PURPOSE_LABEL[p] ?? p])
  );
  return (
    <StackedBarChart
      data={data.series}
      keys={data.purposes}
      labels={labels}
      height={260}
    />
  );
}

const FEEDBACK_LABEL: Record<string, string> = {
  useful: "Avaliação positiva",
  not_useful: "Avaliação negativa",
  no_rating: "Sem avaliação",
};

// Palette: green for positive, gray for "sem avaliação", red for negative.
// Order matches the API's `keys` (useful → no_rating → not_useful) so the
// stack reads positive-at-bottom → neutral → negative-at-top.
const FEEDBACK_COLORS = [
  "var(--color-accent)",
  "var(--color-border)",
  "#F26B6B",
];

function FeedbackChart() {
  const data = useSeries<FeedbackSeries>(
    "/api/admin/series/verifications-by-feedback?days=30"
  );

  if (data === null) return <ChartSkeleton />;
  if (data === "error") {
    return (
      <div className="grid h-[260px] place-items-center rounded-2xl border border-border bg-surface text-sm text-text-tertiary">
        Sem dados no período.
      </div>
    );
  }
  return (
    <StackedBarChart
      data={data.series}
      keys={data.keys}
      labels={FEEDBACK_LABEL}
      colors={FEEDBACK_COLORS}
      height={260}
    />
  );
}

function ChartSkeleton() {
  return (
    <div className="grid h-[260px] place-items-center rounded-2xl border border-border bg-surface">
      <Loader2 size={20} className="animate-spin text-text-tertiary" aria-hidden />
    </div>
  );
}

type Row = {
  id: string;
  target_type: "pf" | "pj";
  target_document: string;
  target_name_provided: string | null;
  declared_purpose: string;
  status: string;
  amount_cents: number;
  discount_cents: number;
  applied_coupon_code: string | null;
  requester_name: string | null;
  requester_email: string | null;
  created_at: string;
  completed_at: string | null;
};

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso.replace(" ", "T") + "Z").toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDoc(type: "pf" | "pj", doc: string): string {
  if (type === "pf" && doc.length === 11) {
    return `${doc.slice(0, 3)}.${doc.slice(3, 6)}.${doc.slice(6, 9)}-${doc.slice(9)}`;
  }
  if (type === "pj" && doc.length === 14) {
    return `${doc.slice(0, 2)}.${doc.slice(2, 5)}.${doc.slice(5, 8)}/${doc.slice(8, 12)}-${doc.slice(12)}`;
  }
  return doc;
}

const STATUS_STYLE: Record<string, string> = {
  pending: "bg-surface-alt text-text-tertiary border-border",
  paid: "bg-accent-soft text-accent border-accent/30",
  processing: "bg-warning/10 text-warning border-warning/30",
  completed: "bg-accent-soft text-accent border-accent/30",
  failed: "bg-danger/10 text-danger border-danger/30",
  refunded: "bg-surface-alt text-text-tertiary border-border",
};

const COLUMNS: Column<Row>[] = [
  {
    header: "Alvo",
    width: "26%",
    cell: (r) => (
      <div>
        <p className="truncate font-medium text-text">
          {r.target_name_provided ?? formatDoc(r.target_type, r.target_document)}
        </p>
        <p className="truncate text-[11px] text-text-tertiary">
          {r.target_type.toUpperCase()} · {formatDoc(r.target_type, r.target_document)}
        </p>
      </div>
    ),
  },
  {
    header: "Consultante",
    width: "23%",
    hideBelow: "md",
    cell: (r) => (
      <div>
        <p className="truncate text-text">{r.requester_name ?? "—"}</p>
        <p className="truncate text-[11px] text-text-tertiary">{r.requester_email}</p>
      </div>
    ),
  },
  {
    header: "Finalidade",
    width: "24%",
    hideBelow: "lg",
    cell: (r) => (
      <span className="truncate text-xs text-text-secondary">
        {PURPOSE_LABEL[r.declared_purpose] ?? r.declared_purpose}
      </span>
    ),
  },
  {
    header: "Status",
    width: "12%",
    cell: (r) => (
      <span
        className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${
          STATUS_STYLE[r.status] ?? "bg-surface-alt text-text-tertiary border-border"
        }`}
      >
        {r.status}
      </span>
    ),
  },
  {
    header: "Datas",
    width: "15%",
    hideBelow: "sm",
    cell: (r) => (
      <div>
        <p className="text-[11px] text-text-secondary">
          {formatDateTime(r.created_at)}
        </p>
        {r.completed_at && (
          <p className="text-[10px] text-text-tertiary">
            ✓ {formatDateTime(r.completed_at)}
          </p>
        )}
      </div>
    ),
  },
];

export default function AdminConsultasPage() {
  return (
    <AdminPage
      eyebrow="Consultas"
      title="Histórico Zarv ID"
      description="Todas as consultas feitas pelo sistema. Cada linha é uma chamada ao Zarv ID com seu estado atual."
    >
      <div className="mb-8 grid gap-8 lg:grid-cols-2">
        <section className="min-w-0">
          <h2 className="mb-3 text-[11px] font-medium uppercase tracking-[0.2em] text-text-tertiary">
            Últimos 30 dias · por finalidade
          </h2>
          <PurposeChart />
        </section>
        <section className="min-w-0">
          <h2 className="mb-3 text-[11px] font-medium uppercase tracking-[0.2em] text-text-tertiary">
            Últimos 30 dias · avaliações
          </h2>
          <FeedbackChart />
        </section>
      </div>
      <AdminTable<Row>
        endpoint="/api/admin/verifications"
        itemsKey="verifications"
        rowKey={(r) => r.id}
        columns={COLUMNS}
        searchPlaceholder="Nome do alvo, documento, e-mail ou nome do consultante"
        emptyTitle="Nenhuma consulta registrada"
      />
    </AdminPage>
  );
}
